import './chooseImage.scss';
import { lang } from '@umu/ukit-lang';
import React from 'react';
import { isUmuWx } from '@umu/umu-ua';
import { If } from 'common/src/components';
import { UploadLoading } from '../../../@uploadLoading/uploadLoading';
import { LANG } from '../../../../@utils/webview';

import UmuWebview from '@umu/umu-webview';
import { $window } from '@umu/umu-dom';
import { getGuid } from '@umu/umu-util';
import { uploadImageToServer } from './imgUtilService';
import { PhotoBrowserUploader, IEndData } from '../photoBrowserUploader/photoBrowserUploader';
import { EMediaType } from '@umu/umu-types';
import { reportError } from 'common/src/services/errorLogger';

function gcd(a: number, b: number): number {
    return b ? gcd(b, a % b) : a;
}

function decimalToFraction(decimal: number) {
    const len = decimal.toString().split('.')[1]?.length || 0;
    const denominator = Math.pow(10, len);
    const numerator = decimal * denominator;

    const divisor = gcd(numerator, denominator);

    return `${numerator / divisor}/${denominator / divisor}`;
}

export const MAX_PHOTO_COUNT = 9;

interface IProps {
    teacherId: string;
    children: JSX.Element | string;
    aspect?: number;
    isCrop?: number;
    limit?: number;
    multiple?: boolean;
    currentCount?: number;
    onlyPreview?: boolean;
    noUseAppUpload?: boolean;
    mediaType?: EMediaType;
    onChoose?: (urls: IChooseImageInfo[]) => void;
    onPreview?: (objectUrl: string, file: File) => void;
    onError?: (e: Error) => void;
    onBrowserSelectReady?: (files: Array<{ file: File; previewUrl: string }>) => void;
}

export interface IChooseImageInfo {
    id: string;
    url: string;
    desc: string;
    file_size?: number;
    resource_id?: string;
    file_name?: string;
}

interface IPhoto {
    id: string;
    url: string;
    desc: string;
    resource_id?: string;
}

export function ChooseImage(props: IProps) {
    const {
        aspect,
        multiple,
        onlyPreview,
        noUseAppUpload,
        isCrop = 0,
        currentCount = 0,
        mediaType = EMediaType.PIC_WEIKE,
        onError,
    } = props;

    const onBrowserSelectReady = (
        files: {
            file: File;
            previewUrl: string;
        }[],
    ) => {
        onlyPreview && hideLoading();
        props.onBrowserSelectReady && props.onBrowserSelectReady(files);
    };

    // getPhoto   type : camera album  count : 0 不限 1 单选 其他 多选限制几张
    // receivePhoto   success 1 ， 旧 imgUrl, 新 imgArr [url ,,,,,]
    // 如果是 UMU APP 里打开则不使用微信
    const chooseImgInWebview = () => {
        UmuWebview.registerHandler('receivePhoto', (data, _callback) => {
            if (!data.success) {
                return;
            }
            if (data.imgUrl) {
                localFileUploadSuccessCallback([data.imgUrl]);
            } else if (data.imgArr) {
                const list = data.imgArr || [];
                localFileUploadSuccessCallback(list);
            }
        });

        const param: Record<string, any> = {
            type: ['camera', 'album'],
            isCrop,
            count: multiple ? MAX_PHOTO_COUNT - currentCount : 1,
        };

        if (aspect) {
            param.aspect = decimalToFraction(aspect);
        }

        UmuWebview.callHandler('getPhoto', param, () => void 0, LANG);
    };

    const chooseImgInWx = () => {
        let localIds = [];
        let inFrame = false; // 组件是否在iframe中
        if (self !== top) {
            inFrame = true;
        }
        const topWindow = parent.parent || {};
        const wx = $window.wx || (inFrame && topWindow.wx);
        wx.chooseImage({
            count: multiple ? MAX_PHOTO_COUNT - currentCount : 1,
            sourceType: ['camera', 'album'],
            success(result: any) {
                if (result.errMsg === 'chooseImage:ok') {
                    localIds = result.localIds;
                    if (Array.isArray(localIds)) {
                        savePhotoListInWx(localIds).then((list) => {
                            localFileUploadSuccessCallback(list);
                        });
                    } else {
                        savePhotoListInWx([localIds]).then((list) => {
                            localFileUploadSuccessCallback(list);
                        });
                    }
                }
            },
            fail(e: any) {
                console.warn(e);
                onError && onError(e);
            },
        });
    };

    const choosePhotoInWebview = (e: React.MouseEvent) => {
        e.preventDefault();
        if (noUseAppUpload || !isUmuWx) {
            return;
        }
        if (UmuWebview && UmuWebview.UA_INFO.isUMUWebview) {
            chooseImgInWebview();
            return;
        }
        chooseImgInWx();
    };

    const showLoading = () => {
        UploadLoading.show({ message: lang('正在上传') });
    };

    const hideLoading = () => {
        UploadLoading.hide();
    };

    const localFileSelectedCallback = () => {
        showLoading();
    };

    const localFileUploadSuccessCallback = (urlList: string[]) => {
        const photoList: IPhoto[] = [];
        urlList.forEach((url: string) => {
            photoList.push({
                id: getGuid(),
                url,
                desc: '',
            });
        });
        props.onChoose && props.onChoose(photoList);
    };

    // 微信中上传
    const savePhotoListInWx = (photoList: string[]): Promise<string[]> => {
        const list = photoList.concat([]);
        const completeList: string[] = [];

        return new Promise((resolve, reject) => {
            const syncUpload = () => {
                const item = list.shift();
                // 参数检查
                if (!item) {
                    const error = new Error('[wx uploadImage] localId is empty');
                    reportError({
                        error,
                        componentName: 'ChooseImage',
                    });
                    reject(error);
                    return;
                }

                const localId = item.toString();
                if (!localId || localId.trim() === '') {
                    const error = new Error('[wx uploadImage] localId is invalid: ' + localId);
                    reportError({
                        error,
                        componentName: 'ChooseImage',
                    });
                    reject(error);
                    return;
                }

                // 检查微信对象是否存在
                if (!wx || typeof wx.uploadImage !== 'function') {
                    const error = new Error('[wx uploadImage] wx object or uploadImage method not available');
                    reportError({
                        error,
                        componentName: 'ChooseImage',
                    });
                    reject(error);
                    return;
                }
                wx.uploadImage({
                    localId: localId,
                    isShowProgressTips: 1,
                    success(res) {
                        const mediaId = res.serverId;
                        const done = () => {
                            if (list.length === 0) {
                                resolve(completeList);
                            } else {
                                syncUpload();
                            }
                        };
                        uploadImageToServer({ media_id: mediaId })
                            .then((data) => {
                                // 有一个 sentry 报错，返回的图片无法向右旋转，怀疑返回有问题，打一下日志看一下
                                if (data.error_code !== 0) {
                                    reportError({
                                        error: new Error(`Upload image server error`),
                                        componentName: 'ChooseImage',
                                        additionalInfo: { errorCode: data.error_code, errorMessage: data },
                                    });
                                }
                                completeList.push(data.data.photo_url);
                                done();
                            })
                            .catch(() => {
                                done();
                            });
                    },
                    fail(e) {
                        onError && onError(e);
                        console.warn(e.errMsg);
                    },
                });
            };
            syncUpload();
        });
    };

    const localFileUploadErrorCallback = (e: Error) => {
        onError && onError(e);
        hideLoading();
    };

    const onEnd = (datas: IEndData[]) => {
        const images: IChooseImageInfo[] = datas.map((item) => ({
            id: getGuid(),
            url: item.url,
            desc: '',
            file_name: item.fileName,
            resource_id: item.resourceId,
        }));
        props.onChoose && props.onChoose(images);
        hideLoading();
    };

    // 浏览器环境下的按钮
    const renderBrowserUploadBtn = () => {
        return (
            <div className="browser-upload-btn">
                <PhotoBrowserUploader
                    multiple={!!props.multiple}
                    teacherId={props.teacherId}
                    disabled={false}
                    mediaType={mediaType}
                    limit={props.limit}
                    buttonClass={'file-btn'}
                    onPreview={props.onPreview}
                    onlyPreview={props.onlyPreview}
                    fileSelectedCallback={localFileSelectedCallback}
                    onUploadError={localFileUploadErrorCallback}
                    onEnd={onEnd}
                    onSelectReady={onBrowserSelectReady}
                >
                    {props.children}
                </PhotoBrowserUploader>
            </div>
        );
    };

    return (
        <div className="choose-photo-wrap" onMouseDown={choosePhotoInWebview}>
            <If condition={noUseAppUpload || !isUmuWx}>{renderBrowserUploadBtn()}</If>
            <If condition={!noUseAppUpload && isUmuWx}>
                <>{props.children}</>
            </If>
        </div>
    );
}
